import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  LayoutDashboard, CalendarClock, Wrench, FolderTree, MapPin, FileText, Tags,
  Trophy, Images, HelpCircle, Star, Megaphone, Settings, LogOut, Menu, X, ExternalLink,
  BarChart3, Building2, Power, Loader2,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useSettings, useSettingsCtx } from "@/context/SettingsContext";
import api from "@/lib/api";
import Seo from "@/components/Seo";

function MaintenanceBar() {
  const settings = useSettings();
  const { patch } = useSettingsCtx();
  const [busy, setBusy] = useState(false);
  const on = !!settings?.maintenance_mode;

  async function toggle() {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/maintenance", { enabled: !on });
      patch({ maintenance_mode: !!data.maintenance_mode });
      toast.success(!on ? "Bakım modu açıldı. Ziyaretçiler bakım ekranını görüyor." : "Bakım modu kapatıldı. Site yayında.");
    } catch (e) {
      toast.error("İşlem başarısız. Tekrar deneyin.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      data-testid="admin-maintenance-bar"
      className={`mb-6 flex flex-wrap items-center justify-between gap-3 rounded-xl border px-4 py-3 ${on ? "border-red-500/30 bg-red-500/[0.06]" : "border-emerald-500/25 bg-emerald-500/[0.05]"}`}
    >
      <div className="flex items-center gap-2.5">
        <span className={`h-2.5 w-2.5 rounded-full ${on ? "bg-red-500 shadow-[0_0_10px_rgba(239,68,68,0.7)]" : "bg-emerald-500 shadow-[0_0_10px_rgba(16,185,129,0.6)]"}`} />
        <span data-testid="maintenance-status-label" className="text-sm font-semibold text-white">
          {on ? "🔴 Bakımda" : "🟢 Yayında"}
        </span>
        <span className="hidden text-xs text-neutral-500 sm:inline">
          {on ? "Ziyaretçiler bakım ekranı görüyor; siz gerçek siteyi görebilirsiniz." : "Site tüm ziyaretçilere normal görünüyor."}
        </span>
      </div>
      <button
        onClick={toggle}
        disabled={busy}
        data-testid="maintenance-toggle-btn"
        className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-bold transition-colors disabled:opacity-60 ${on ? "bg-emerald-500 text-black hover:bg-emerald-400" : "bg-red-500 text-white hover:bg-red-400"}`}
      >
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Power className="h-4 w-4" />}
        {on ? "Bakım Modunu Kapat" : "Bakım Modunu Aç"}
      </button>
    </div>
  );
}

const NAV = [
  { to: "/admin", label: "Gösterge Paneli", icon: LayoutDashboard, end: true },
  { to: "/admin/seo-hub", label: "SEO Hub", icon: BarChart3 },
  { to: "/admin/randevular", label: "Randevu Talepleri", icon: CalendarClock },
  { to: "/admin/c/services", label: "Hizmetler", icon: Wrench },
  { to: "/admin/c/categories", label: "Kategoriler", icon: FolderTree },
  { to: "/admin/c/lokasyonlar", label: "İlçe SEO Sayfaları", icon: MapPin },
  { to: "/admin/c/district-hubs", label: "İlçe Hub Sayfaları", icon: Building2 },
  { to: "/admin/c/blogs", label: "Blog Yazıları", icon: FileText },
  { to: "/admin/c/blog-categories", label: "Blog Kategorileri", icon: Tags },
  { to: "/admin/c/case-studies", label: "Uygulamalar", icon: Trophy },
  { to: "/admin/c/before-after", label: "Öncesi / Sonrası", icon: Images },
  { to: "/admin/c/faqs", label: "Sık Sorulan Sorular", icon: HelpCircle },
  { to: "/admin/c/reviews", label: "Yorumlar", icon: Star },
  { to: "/admin/c/campaigns", label: "Kampanyalar", icon: Megaphone },
  { to: "/admin/ayarlar", label: "Site Ayarları", icon: Settings },
];

export default function AdminLayout() {
  const { user, checking, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!checking && !user) navigate("/admin/login", { replace: true });
  }, [checking, user, navigate]);

  if (checking || !user) return <div className="flex min-h-screen items-center justify-center bg-neutral-950 text-neutral-500">Yükleniyor…</div>;

  return (
    <div className="min-h-screen bg-neutral-950">
      <Seo title="Yönetim Paneli | Meguiar's İzmit" noindex />
      {/* Top bar (mobile) */}
      <div className="flex items-center justify-between border-b border-white/10 px-4 py-3 lg:hidden">
        <Link to="/admin" className="flex items-center gap-2">
          <img src="/logo.png" alt="logo" className="h-8 w-8 rounded object-contain" />
          <span className="font-display font-medium text-white">Yönetim</span>
        </Link>
        <button onClick={() => setOpen((o) => !o)} className="rounded-lg border border-white/10 p-2 text-white">{open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}</button>
      </div>

      <div className="lg:flex">
        {/* Sidebar */}
        <aside className={`${open ? "block" : "hidden"} border-b border-white/10 lg:block lg:w-72 lg:shrink-0 lg:border-b-0 lg:border-r lg:min-h-screen`}>
          <div className="hidden items-center gap-3 px-6 py-6 lg:flex">
            <img src="/logo.png" alt="Meguiar's İzmit" className="h-10 w-10 rounded-lg object-contain" />
            <div><p className="font-display font-medium text-white">Meguiar's İzmit</p><p className="text-xs text-gold">Yönetim Paneli</p></div>
          </div>
          <nav className="space-y-1 p-3 lg:px-4">
            {NAV.map((item) => {
              const Icon = item.icon;
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  onClick={() => setOpen(false)}
                  data-testid={`admin-nav-${item.to.split("/").pop()}`}
                  className={({ isActive }) =>
                    `flex items-center gap-3 rounded-xl px-4 py-2.5 text-sm font-medium transition-colors ${isActive ? "bg-gold/10 text-gold" : "text-neutral-400 hover:bg-white/5 hover:text-white"}`
                  }
                >
                  <Icon className="h-[18px] w-[18px]" /> {item.label}
                </NavLink>
              );
            })}
            <div className="my-2 border-t border-white/10" />
            <a href="/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 rounded-xl px-4 py-2.5 text-sm font-medium text-neutral-400 hover:bg-white/5 hover:text-white transition-colors">
              <ExternalLink className="h-[18px] w-[18px]" /> Siteyi Görüntüle
            </a>
            <button onClick={() => { logout(); navigate("/admin/login"); }} data-testid="admin-logout" className="flex w-full items-center gap-3 rounded-xl px-4 py-2.5 text-sm font-medium text-neutral-400 hover:bg-red-500/10 hover:text-red-400 transition-colors">
              <LogOut className="h-[18px] w-[18px]" /> Çıkış Yap
            </button>
          </nav>
        </aside>

        {/* Content */}
        <main className="flex-1 p-5 sm:p-8 lg:p-10">
          <MaintenanceBar />
          <Outlet />
        </main>
      </div>
    </div>
  );
}
